import { isProgramOrder, PROGRAM_PIX_DISCOUNT_PERCENT } from 'pages/WeightLossProgram/constants'

/**
 * Regras do desconto de pagamento em Pix.
 *
 * O `amount` do pedido nem sempre e bruto. Quando um pedido e pago 100% em Pix com desconto, o
 * ms-payments reescreve `cn_orders.amount` e `cn_items.amount` para o valor LIQUIDO e registra o
 * percentual aplicado em `payment_discount_percentage` — ver `services/exchangePricingService.js`
 * no apiCannect.
 *
 * `apply_pix_discount` diz que o pedido **tem direito** ao desconto; `payment_discount_percentage`
 * diz que ele **ja foi aplicado**. Tratar o primeiro como se fosse o segundo era o que produzia
 * desconto sobre desconto: o pedido 152043 exibiu R$ 922,81 (971,38 x 0,95) onde o correto era
 * R$ 971,38 (1.022,50 x 0,95).
 */

/** Percentual de desconto de pagamento já embutido no `amount` do pedido. 0 quando não há. */
export function getAppliedPaymentDiscountPercentage(order: any): number {
  return Number(order?.payment_discount_percentage ?? 0) || 0
}

/** Se o `amount` do pedido já vem líquido de desconto de pagamento. */
export function isPaymentDiscountAlreadyApplied(order: any): boolean {
  return getAppliedPaymentDiscountPercentage(order) > 0
}

/**
 * Se o desconto de Pix ainda pode ser calculado sobre o `amount` do pedido.
 *
 * Pedidos servidos por uma API que ainda não expõe `payment_discount_percentage` caem no
 * comportamento anterior — o campo ausente vale 0 e o desconto é aplicado como antes. Isso mantém a
 * ordem de deploy livre: o front pode subir antes do backend sem regressão.
 */
export function canApplyPixDiscount(order: any): boolean {
  if (import.meta.env.VITE_APP_APPLY_PIX_DISCOUNT !== 'enabled') return false
  if (!order?.apply_pix_discount) return false
  // Reenvio cobra a taxa combinada com o paciente, não o total do pedido.
  if (order?.origin === 'REENVIO') return false

  return !isPaymentDiscountAlreadyApplied(order)
}

type TPixDiscountOrder = { is_program_order?: boolean; apply_pix_discount?: boolean | null } | null | undefined

/**
 * Desconto do Pix à vista para exibição e cobrança no carrinho v2. O programa de perda de peso tem o próprio
 * (fixo, igual ao da API) e só vale enquanto o `amount` ainda é bruto; os demais pedidos seguem o desconto geral
 * da plataforma (envs + flag do pedido), como antes.
 */
export function getPixDiscount(order: TPixDiscountOrder & { payment_discount_percentage?: number | null }) {
  if (isProgramOrder(order)) {
    return { applied: !isPaymentDiscountAlreadyApplied(order), percentage: PROGRAM_PIX_DISCOUNT_PERCENT }
  }

  return {
    applied: import.meta.env.VITE_APP_APPLY_PIX_DISCOUNT === 'enabled' && Boolean(order?.apply_pix_discount),
    percentage: Number(import.meta.env.VITE_APP_PIX_DISCOUNT_PERCENTAGE || 5)
  }
}

/** Valor com o desconto aplicado, no mesmo arredondamento da API (centavos). */
export function applyPixDiscountToAmount(amount: number, percentage: number): number {
  return Math.round(amount * 100 * ((100 - percentage) / 100)) / 100
}
